import { LinkButton } from '@cloudflare/kumo';
import { ArrowRight, Globe, Plus } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { createRoute, Link, Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { sitesQuery } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { PendingRequests } from '../components/pending-requests.tsx';
import { Route as parent } from './signed-in.tsx';
export const Route = createRoute({ getParentRoute: () => parent, path: '/', component: Dashboard });

function SiteFavicon({ src }: { src: string | null }) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  return (
    <div className="w-12.5 h-12.5 bg-kumo-tint text-kumo-brand grid place-items-center rounded-[12px] overflow-hidden">
      {src && failedSrc !== src ? (
        <img src={src} alt="" className="w-full h-full object-contain" onError={() => setFailedSrc(src)} />
      ) : (
        <Globe size={26} weight="light" />
      )}
    </div>
  );
}

function Dashboard() {
  const sites = useQuery(sitesQuery);
  const list = sites.data ?? [];
  if (sites.isSuccess && sites.fetchStatus === 'idle' && list.length === 0)
    return <Navigate to="/new" replace />;
  return (
    <main className="mx-auto my-0 px-5.5 py-8 max-w-310 min-[761px]:px-10 min-[761px]:pt-12 min-[761px]:pb-20">
      {list.length > 0 && (
        <div className="flex justify-between items-start gap-5 mb-9.5 [&_h1]:mx-0 [&_h1]:mt-0 [&_h1]:mb-2.5 [&_h1]:text-[26px] [&_h1]:leading-[1.2] [&_h1]:font-semibold [&_h1]:tracking-[-0.8px] [&_.muted]:max-w-42.5 min-[761px]:items-center min-[761px]:[&_h1]:m-0 min-[761px]:[&_h1]:text-[30px] min-[761px]:[&_h1]:leading-[1.2] min-[761px]:[&_h1]:font-semibold min-[761px]:[&_h1]:tracking-[-0.8px] min-[761px]:[&_.muted]:max-w-none">
          <div>
            <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]">My sites</h1>
          </div>
          <LinkButton href="/new" variant="primary">
            <Plus size={18} />
            Create site
          </LinkButton>
        </div>
      )}
      <ErrorNotice error={sites.error} />
      <PendingRequests className="mb-6" />
      {sites.isPending || (sites.isFetching && list.length === 0) ? (
        <Loading />
      ) : list.length > 0 ? (
        <div className="grid grid-cols-1 gap-5 min-[761px]:grid-cols-2 min-[1051px]:grid-cols-3">
          {list.map((site) => (
            <Link
              key={site.id}
              to="/sites/$siteId/pages"
              params={{ siteId: site.id }}
              className="p-6 border border-kumo-line bg-kumo-base rounded-[13px] [transition:border-color_0.15s,_box-shadow_0.15s] [&:hover]:border-app-accent-border [&:hover]:shadow-[0_6px_22px_var(--color-kumo-shadow-drop)] [&_h2]:mx-0 [&_h2]:mt-0 [&_h2]:mb-1.5 [&_h2]:text-[18px] [&_h2]:font-semibold [&_h2]:tracking-[-0.35px] [&_>_p]:text-kumo-subtle [&_>_p]:text-[12px] [&_>_p]:overflow-hidden [&_>_p]:text-ellipsis [&_>_p]:whitespace-nowrap"
            >
              <div className="flex justify-between mb-7 items-center">
                <SiteFavicon src={site.favicon} />
                <span className="px-2.5 py-[3px] border border-kumo-line rounded-[20px] text-[11px] text-kumo-subtle">
                  {!site.published
                    ? 'Draft'
                    : site.unpublishedChangeCount > 0
                      ? 'Published with unpublished changes'
                      : 'Published'}
                </span>
              </div>
              <h2 className="text-[20px] font-semibold tracking-[-0.35px]">{site.name || 'Untitled site'}</h2>
              <p className="leading-[1.65]">{site.domain}</p>
              <div className="border-t border-t-kumo-line flex justify-end mt-[25px] pt-4 text-[11px] text-kumo-subtle [&_span:last-child]:flex [&_span:last-child]:gap-2 [&_span:last-child]:items-center">
                <span>
                  Open site <ArrowRight size={16} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      ) : null}
    </main>
  );
}
